import type * as THREE from "three";
import type { ArtStyleId } from "../../settings";

// An art style (#75) owns every look decision: the renderer builds its
// materials, outlines, lights, environment, sky, fog, tone mapping and
// shadows from the active one instead of hard-coded constants. The sim never
// sees it. Every field is required, so a style missing one doesn't compile.

/**
 * What a surface is made of, so a style can treat each kind its own way
 * (the cartoon style paints them all the same smooth plastic).
 */
export type Surface =
  | "plastic"
  | "paint"
  | "concrete"
  | "plywood"
  | "metal"
  | "paintedMetal"
  | "grass"
  | "glass"
  | "skin"
  | "cloth"
  | "denim"
  | "leather"
  | "hair";

export type MaterialParams = THREE.MeshStandardMaterialParameters;

/** How expensive a style is to draw: the performance tier it declares. */
export type CostTier = "low" | "medium" | "high";

/** Where scene reflections and ambient bounce come from. */
export type EnvironmentKind =
  /** three's RoomEnvironment: a soft studio box, the same for every map. */
  | "studio"
  /** The map's own sky gradient with a bright sun, so the bounce matches the sky. */
  | "sky";

/**
 * Screen-space passes drawn after the scene (#75), through three's
 * EffectComposer. A style with every pass null renders straight to the
 * canvas, exactly as before the chain existed.
 */
export interface PostChain {
  /** Ground-truth ambient occlusion (GTAO): contact darkening in corners and under things. High quality only. */
  ao: { radius: number; intensity: number } | null;
  /** A light bloom on the brightest highlights. High quality only. */
  bloom: { strength: number; radius: number; threshold: number } | null;
  /** Color grading after tone mapping (curves-style): contrast, saturation, warmth and a vignette. High quality only: Low draws no post at all. */
  grade: { contrast: number; saturation: number; warmth: number; vignette: number } | null;
  /**
   * A tilt-shift blur (#76, clay): a sharp band across the screen at `focus`
   * (0 bottom, 1 top), `band` high, blurring up to `blur` pixels toward the
   * top and bottom, so the park reads as a miniature. High quality only.
   */
  tiltShift: { focus: number; band: number; blur: number } | null;
  /**
   * The map's mood as a color look-up table (#179, realistic: `render/mood.ts`),
   * blended in at `intensity`, after the grade. High quality only.
   */
  mood: { intensity: number } | null;
  /**
   * Depth of field for the finisher camera (#179, realistic): while it's
   * punched in, everything off the rider's distance blurs, `aperture` per
   * metre up to `maxBlur` (a share of the screen). Off the rest of the time,
   * so it costs nothing then. High quality only.
   */
  dof: { aperture: number; maxBlur: number } | null;
}

/** Motes drifting in the air around the rider, catching the light (#179, realistic). High quality only. */
export interface DustLook {
  count: number;
  /** Mote size in metres. */
  size: number;
  opacity: number;
  /** How far out from the camera's target they fill, in metres. */
  radius: number;
}

/**
 * The rider's build in a style (#75): proportions and detail laid over the
 * same bones. Bone lengths and joint positions are shared with the pose
 * code's IK (`rider/look.ts`), so a build changes how the rider looks, never
 * how it moves. `NEUTRAL_BUILD` is the cartoon's chunky toy.
 */
export interface RiderBuild {
  /** Head size (face, hair and headwear together); the head sits lower on the neck to match. */
  head: number;
  /** Eye size within the head: big toy eyes at 1. */
  eyes: number;
  /** The toy face (glossy eyes with catch-lights, a drawn grin) or a natural one (lips, a longer jaw, a finer nose). */
  face: "toy" | "natural";
  /** Limb and neck thickness. */
  limbs: number;
  /** Chest depth (front to back) and height (the toy capsule's). */
  chest: { depth: number; height: number };
  /**
   * The toy's capsule chest, or a shaped torso (`rider/trunk.ts`): a waist,
   * shoulders out to where the arms pivot, sloping up into the neck.
   */
  torso: "capsule" | "shaped";
  /**
   * How limbs meet: the toy's capsules with a ball at the elbow, or one
   * tapered tube per bone (a swell at the bicep and calf, narrow at the
   * wrist and ankle) with rounded ends on the joints, as wide as the next
   * tube starts, so a bent elbow or knee reads as one limb.
   */
  joints: "ball" | "tapered";
  /** Round mittens, or a flatter palm with four fingers and a thumb. */
  hands: "mitten" | "fingers";
  /** Footwear size: length, then height and width. */
  shoes: { length: number; profile: number };
  /**
   * The toy's footwear (rounded boxes and balls), or natural footwear lofted
   * from a foot's outline (`rider/feet-natural.ts`: a vulcanized sole,
   * laces, a boot heel and shaft).
   */
  feet: "toy" | "natural";
  /**
   * Segments on the rider's round pieces (spheres, capsules, tubes, rings),
   * as a fraction of the toy's: 1 is smooth, lower is low-poly facets (#76,
   * retro's PS1 rider). `rider/geom.ts`'s `facets` applies it.
   */
  facets: number;
}

export const NEUTRAL_BUILD: RiderBuild = {
  head: 1,
  eyes: 1,
  face: "toy",
  limbs: 1,
  chest: { depth: 1, height: 1 },
  torso: "capsule",
  joints: "ball",
  hands: "mitten",
  shoes: { length: 1, profile: 1 },
  feet: "toy",
  facets: 1,
};

export interface ArtStyle {
  id: ArtStyleId;
  /** Shown in Settings. */
  name: string;
  /** One line on what the style is going for. */
  pitch: string;
  cost: CostTier;

  /** Makes the material for one surface; `params` are the call site's color-independent choices (maps, roughness...). */
  material(color: THREE.ColorRepresentation, params: MaterialParams, surface: Surface): THREE.MeshStandardMaterial;

  outlines: {
    /** Ink outline width multiplier on meshes; 0 draws none. */
    scale: number;
    ink: THREE.ColorRepresentation;
    /** Opacity of the thin crease lines on park pieces; 0 draws none. */
    edges: number;
  };

  lighting: {
    sunColor: THREE.ColorRepresentation;
    /** Multiplies the map's sun intensity. */
    sun: number;
    /** Multiplies the map's hemisphere (sky / ground) light intensity. */
    hemi: number;
    environment: EnvironmentKind;
    /** How much the environment lights and reflects, outdoors and indoors. */
    envIntensity: { outdoor: number; indoor: number };
  };

  sky: {
    /** The puffy clouds round an outdoor park. */
    clouds: boolean;
  };

  /** Multiplies the map's fog distances (lower is hazier). */
  fog: { near: number; far: number };

  toneMapping: THREE.ToneMapping;
  exposure: number;

  post: PostChain;

  /** Dust in the air (#179); null for none. */
  dust: DustLook | null;

  /**
   * Draw the frame at a fixed number of lines and blow it up with hard pixel
   * edges (#76, retro); null draws at the screen's own resolution.
   */
  resolution: { lines: number } | null;

  rider: RiderBuild;

  shadows: {
    mapSize: number;
    /** PCF filter radius: bigger is softer. */
    radius: number;
    bias: number;
    normalBias: number;
  };
}
